import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { Inline, Stack } from '@atlaskit/primitives/compiled';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { type CarouselCardProps } from '@post-office/browser--townsquare/common/carouselBaseCard/types';

import { type TwcCardMessageVariants } from '@post-office/browser--townsquare/common/utils';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useMemo, useRef, useState } from 'react';

import { CarouselCompleteState } from './components/CarouselCompleteState';

import { CarouselDismissalFlag } from './components/CarouselDismissalFlag';

import { CarouselWrapper } from './components/CarouselWrapper';

import { MAX_NUM_CARDS } from './constants';

import { getNumCardsToShow } from './utils';

type PlacementDefinition = CustomRenderer<CarouselCardProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'townsquare-onboarding-widget',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<CarouselCardProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<CarouselCardProps>;
	},
	U
> = placement.createFrontend;

type MessageMetaType = {
	displayUrls: Record<string, string>;
	variants: TwcCardMessageVariants;
	completedStateSeen?: boolean;
	carouselExpired?: boolean;
	errorFetchingTapTraits?: boolean;
	/**
	 * @deprecated The twcg-984-home-onboarding-carousel experiment was cleaned up in STARB-2627.
	 * The backend no longer sets this value so it is kept here only for type compatibility with
	 * any stale messageMeta payloads that may still carry it. The render guard that checked
	 * `carouselExpVal !== 'variation'` has been removed because the backend will never set it
	 * to 'variation' — leaving the guard in would cause the placement to always return null.
	 */
	carouselExpVal?: 'control' | 'variation' | 'not-enrolled';
};

const EMPTY_MESSAGE_META: MessageMetaType = {
	displayUrls: {},
	variants: {},
};

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<CarouselCardProps>;
	},
	NoProps
> = createFrontend((Messages) => () => {
	const containerRef = useRef<HTMLDivElement>(null);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const [numCardsToShow, setNumCardsToShow] = useState(MAX_NUM_CARDS);
	const [firstIndexOnScreen, setFirstIndexOnScreen] = useState(0);
	// Note this is actually [last index on screen + 1] as slice is not inclusive of the last index given, but for lack of better name we'll keep it as lastIndexOnScreen
	const [lastIndexOnScreen, setLastIndexOnScreen] = useState(numCardsToShow);
	const [messages, setMessages] = useState<EligibleMessage<Record<string, never>>[]>([]);
	const [messageMeta, setMessageMeta] = useState<MessageMetaType>(EMPTY_MESSAGE_META);
	const [isCarouselOpen, setIsCarouselOpen] = useState(false);
	const [showDismissalFlag, setShowDismissalFlag] = useState(false);

	const NUM_OF_CARDS = useMemo(() => messages.length, [messages]);

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<Record<string, never>>>;
		messageMeta: MessageMetaType;
	}>('/');

	useEffect(() => {
		if (isLoading || error) {
			return;
		}
		setMessages(data.messages);
		setMessageMeta(data.messageMeta ?? EMPTY_MESSAGE_META);
		if (data.messages.length > 0) {
			setIsCarouselOpen(true);
		}
	}, [data, error, isLoading]);

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => {
			if (entries[0]) {
				const width = entries[0].contentRect.width;
				const newNumCardsToShow = getNumCardsToShow(width);
				setNumCardsToShow(newNumCardsToShow);
			}
		});

		if (containerRef?.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	});

	useEffect(() => {
		setLastIndexOnScreen(firstIndexOnScreen + numCardsToShow);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [numCardsToShow]);

	const handlePrevButtonClick = () => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'carouselArrow',
			actionSubjectId: 'townsquareOnboardingCarouselArrow',
			attributes: {
				direction: 'back',
			},
		});
		const NUM_PREV_CARDS = firstIndexOnScreen;
		if (NUM_PREV_CARDS >= numCardsToShow) {
			// Can populate full placement with new cards
			setFirstIndexOnScreen(firstIndexOnScreen - numCardsToShow);
			setLastIndexOnScreen(lastIndexOnScreen - numCardsToShow);
		} else if (NUM_PREV_CARDS > 0) {
			// Can populate partial placement with new cards
			setFirstIndexOnScreen(firstIndexOnScreen - NUM_PREV_CARDS);
			setLastIndexOnScreen(lastIndexOnScreen - NUM_PREV_CARDS);
		}
	};

	const handleNextButtonClick = () => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'carouselArrow',
			actionSubjectId: 'townsquareOnboardingCarouselArrow',
			attributes: {
				direction: 'forward',
			},
		});
		const NUM_NEXT_CARDS = NUM_OF_CARDS - lastIndexOnScreen;
		if (NUM_NEXT_CARDS >= numCardsToShow) {
			// Can populate full placement with new cards
			setFirstIndexOnScreen(firstIndexOnScreen + numCardsToShow);
			setLastIndexOnScreen(lastIndexOnScreen + numCardsToShow);
		} else if (NUM_NEXT_CARDS > 0) {
			// Can populate partial placement with new cards
			setFirstIndexOnScreen(firstIndexOnScreen + NUM_NEXT_CARDS);
			setLastIndexOnScreen(lastIndexOnScreen + NUM_NEXT_CARDS);
		}
	};

	const handleDismiss = (messageInstanceId: string, messageTemplateId: string): void => {
		setMessages((prevMessages) => {
			const filteredMessages = prevMessages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);

			return filteredMessages;
		});
		delete messageMeta.variants[messageTemplateId];
	};

	// Updating variants in local state for getting started card.
	// This is not required for the rest of the card as they open links in the same tab
	// but trying to avoid custom logic for 1 card
	const updateLocalStateVariant = (messageTemplateId: string) => {
		setMessageMeta((prevMessageMeta) => ({
			...prevMessageMeta,
			variants: { ...prevMessageMeta.variants, [messageTemplateId]: 'completed' },
		}));
	};

	const isCarouselComplete = useMemo(() => {
		// Early exit: no messages on page load or carousel complete state seen
		if (data?.messages?.length === 0 || messageMeta.completedStateSeen) {
			return false;
		}

		// All messages are complete on page load
		if (
			!messageMeta.completedStateSeen &&
			Object.keys(messageMeta.variants).length > 0 &&
			Object.values(messageMeta.variants).every((variant) => variant === 'completed')
		) {
			return true;
		}

		// handle dismissing the last active message
		if (isCarouselOpen) {
			return (
				messages.find((message) => messageMeta.variants[message.messageTemplateId] === 'active') ===
				undefined
			);
		}

		return false;
	}, [messageMeta, messages, isCarouselOpen, data]);

	const { visibleMessages, invisibleMessages } = useMemo(() => {
		return {
			visibleMessages: messages.slice(firstIndexOnScreen, lastIndexOnScreen),
			invisibleMessages: messages.filter(
				(_message, key) => !(key < lastIndexOnScreen && key >= firstIndexOnScreen),
			),
		};
	}, [messages, firstIndexOnScreen, lastIndexOnScreen]);

	if (
		isLoading ||
		error ||
		messageMeta.errorFetchingTapTraits ||
		messageMeta.completedStateSeen ||
		messageMeta.carouselExpired
	) {
		return null;
	}

	return (
		<>
			{isCarouselComplete && <CarouselCompleteState />}
			{showDismissalFlag && (
				<CarouselDismissalFlag handleDismissFlag={() => setShowDismissalFlag(false)} />
			)}
			{messages.length > 0 && !isCarouselComplete && (
				<Stack space="space.0">
					<CarouselWrapper
						containerRef={containerRef}
						numCardsToShow={numCardsToShow}
						numTotalCards={NUM_OF_CARDS ?? 0}
						firstIndexOnScreen={firstIndexOnScreen}
						lastIndexOnScreen={lastIndexOnScreen}
						handleNextButtonClick={handleNextButtonClick}
						handlePrevButtonClick={handlePrevButtonClick}
						cardsListForAnalytics={messages.map((message) => message.messageTemplateId)}
						closeCarousel={() => {
							setIsCarouselOpen(false);
							setShowDismissalFlag(true);
						}}
					>
						<Inline space="space.200">
							{visibleMessages.map((props: EligibleMessage, key: number) => {
								const templateId = props.messageTemplateId;
								const variant = messageMeta.variants[templateId] ?? 'active';
								const displayUrl = messageMeta.displayUrls[templateId];
								return (
									<Messages.render
										key={`visible_${key}`}
										{...props}
										variant={variant}
										displayUrl={displayUrl}
										dismiss={() => handleDismiss(props.messageInstanceId, props.messageTemplateId)}
										isVisible={true}
										isCarouselOpen={isCarouselOpen}
										onClick={() => updateLocalStateVariant(templateId)}
									/>
								);
							})}
						</Inline>
					</CarouselWrapper>
					<Inline space="space.0">
						{invisibleMessages.map((props: EligibleMessage, key: number) => (
							<Messages.render
								key={`hidden_${key}`}
								{...props}
								variant={'active'}
								displayUrl={messageMeta.displayUrls[props.messageTemplateId]}
								dismiss={() => handleDismiss(props.messageInstanceId, props.messageTemplateId)}
								isVisible={false}
								isCarouselOpen={isCarouselOpen}
							/>
						))}
					</Inline>
				</Stack>
			)}
		</>
	);
});
